import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import { uploadToCloudinary } from '../utils/cloudinary';
import { getAdLink } from '../utils/adUtils';
import Spinner from '../components/Spinner';
import './Profile.css';

const Profile = () => {
    const navigate = useNavigate();
    const { user, updateUserProfile, logout } = useAuth();
    const [myAds, setMyAds] = useState([]);
    const [loadingAds, setLoadingAds] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeData, setUpgradeData] = useState({ vendorName: '', slug: '' });
    const [upgradeError, setUpgradeError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [profileData, setProfileData] = useState({
        fullName: user?.fullName || '',
        phone: user?.phone || '',
        vendorName: user?.vendorName || '',
        slug: user?.slug || ''
    });

    const isCorporate = user?.userType === 'Corporate' || user?.role === 'admin';

    useEffect(() => {
        if (!user) return;

        const fetchUserAds = async () => {
            try {
                const q = query(collection(db, "ads"), where("userId", "==", user.uid));
                const querySnapshot = await getDocs(q);
                const fetchedAds = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                // Sort by createdAt descending
                fetchedAds.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
                setMyAds(fetchedAds);
            } catch (error) {
                console.error("Error fetching user ads:", error);
            } finally {
                setLoadingAds(false);
            }
        };

        fetchUserAds();
        setProfileData({
            fullName: user.fullName || '',
            phone: user.phone || '',
            vendorName: user.vendorName || '',
            slug: user.slug || ''
        });
    }, [user]);

    const handleProfileChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value
        });
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        try {
            const extraData = {};
            if (isCorporate) {
                extraData.vendorName = profileData.vendorName;
                extraData.slug = profileData.slug;
            }
            await updateUserProfile(profileData.fullName, profileData.phone, extraData);
            setIsEditing(false);
            setSuccessMessage('Profile updated successfully!');
        } catch (error) {
            console.error("Error updating profile:", error);
            alert('Failed to update profile.');
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setUploadingLogo(true);
            const url = await uploadToCloudinary(file);
            if (url) {
                await updateUserProfile(user.fullName, user.phone, { logo: url });
                setSuccessMessage('Logo updated successfully!');
            }
        } catch (error) {
            console.error("Error uploading logo:", error);
            alert('Failed to upload logo.');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleCoverUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setUploadingCover(true);
            const url = await uploadToCloudinary(file);
            if (url) {
                await updateUserProfile(user.fullName, user.phone, { coverPhoto: url });
                setSuccessMessage('Cover photo updated successfully!');
            }
        } catch (error) {
            console.error("Error uploading cover photo:", error);
            alert('Failed to upload cover photo.');
        } finally {
            setUploadingCover(false);
        }
    };

    const handleDeleteAd = async (adId) => {
        if (window.confirm("Are you sure you want to delete this ad? This action cannot be undone.")) {
            try {
                await deleteDoc(doc(db, "ads", adId));
                setMyAds(prev => prev.filter(ad => ad.id !== adId));
                setSuccessMessage("Ad deleted successfully.");
            } catch (error) {
                console.error("Error deleting ad:", error);
                alert("Failed to delete ad.");
            }
        }
    };

    const handleUpgradeSubmit = async (e) => {
        e.preventDefault();
        setUpgradeError('');

        if (!upgradeData.vendorName.trim() || !upgradeData.slug.trim()) {
            return setUpgradeError('Both Vendor Name and Profile Slug are required.');
        }

        const validSlug = upgradeData.slug.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();

        try {
            await updateUserProfile(user.fullName, user.phone, {
                userType: 'Corporate',
                vendorName: upgradeData.vendorName,
                slug: validSlug,
                logo: '',
                coverPhoto: ''
            });
            setShowUpgradeModal(false);
            setSuccessMessage('Successfully upgraded to a Corporate account!');
        } catch (error) {
            console.error("Error upgrading account:", error);
            setUpgradeError('Failed to upgrade account.');
        }
    };

    if (!user) return <div style={{ textAlign: 'center', padding: '2rem' }}>Please log in first.</div>;

    return (
        <div className="profile-container">
            {/* User Profile Info / Edit Form */}
            <div className="profile-section">
                {/* Corporate Cover Photo */}
                {isCorporate && (
                    <div className="profile-cover-area">
                        {user.coverPhoto ? (
                            <img src={user.coverPhoto} alt="Cover" className="profile-cover-img" />
                        ) : (
                            <div className="profile-cover-placeholder">
                                <div className="cover-gradient-pattern"></div>
                            </div>
                        )}
                        <label className="profile-cover-upload-btn">
                            <i className="fa-solid fa-camera"></i>
                            {uploadingCover ? 'Uploading...' : 'Change Cover'}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleCoverUpload}
                                style={{ display: 'none' }}
                                disabled={uploadingCover}
                            />
                        </label>
                    </div>
                )}

                <div className="profile-card">
                    {/* Corporate Logo */}
                    {isCorporate && (
                        <div className="profile-logo-area">
                            {user.logo ? (
                                <img src={user.logo} alt="Logo" className="profile-logo-img" />
                            ) : (
                                <div className="profile-logo-placeholder">
                                    <i className="fa-solid fa-building"></i>
                                </div>
                            )}
                            <label className="profile-logo-upload-btn">
                                <i className="fa-solid fa-camera"></i>
                                {uploadingLogo ? '...' : ''}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploadingLogo}
                                />
                            </label>
                        </div>
                    )}

                    <div className="profile-header">
                        <h2 className="profile-title">My Profile</h2>
                        <div className="profile-actions">
                            {isCorporate && (
                                <span className="profile-type-badge corporate">
                                    <i className="fa-solid fa-building"></i> Corporate
                                </span>
                            )}
                            <button
                                className="btn-secondary"
                                onClick={() => setIsEditing(!isEditing)}
                            >
                                {isEditing ? 'Cancel' : 'Edit Profile'}
                            </button>
                        </div>
                    </div>

                    {isEditing ? (
                        <form onSubmit={handleSaveProfile} className="profile-edit-form">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={profileData.fullName}
                                    onChange={handleProfileChange}
                                    required
                                />
                            </div>
                            {isCorporate && (
                                <>
                                    <div className="form-group">
                                        <label>Vendor / Business Name</label>
                                        <input
                                            type="text"
                                            name="vendorName"
                                            value={profileData.vendorName}
                                            onChange={handleProfileChange}
                                            placeholder="e.g. ABC Motors"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Profile Slug</label>
                                        <input
                                            type="text"
                                            name="slug"
                                            value={profileData.slug}
                                            onChange={(e) => setProfileData({ ...profileData, slug: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase() })}
                                            placeholder="e.g. abc-motors (no spaces)"
                                            required
                                        />
                                    </div>
                                </>
                            )}
                            <div className="form-group">
                                <label>Phone</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={profileData.phone}
                                    onChange={handleProfileChange}
                                    required
                                />
                            </div>
                            <button type="submit" className="btn-save">Save Changes</button>
                        </form>
                    ) : (
                        <div className="user-info">
                            <div className="info-item">
                                <label>Full Name</label>
                                <p>{user.fullName || 'Not set'}</p>
                            </div>
                            {isCorporate && (
                                <div className="info-item">
                                    <label>Profile Slug</label>
                                    <p>{user.slug || 'Not set'}</p>
                                </div>
                            )}
                            <div className="info-item">
                                <label>Email</label>
                                <p>{user.email}</p>
                            </div>
                            <div className="info-item">
                                <label>Phone</label>
                                <p>{user.phone || 'Not set'}</p>
                            </div>
                            <div className="info-item">
                                <label>Account Type</label>
                                <p>{user.userType || 'Individual'}</p>
                            </div>
                            <div className="info-item">
                                <label>Member Since</label>
                                <p>{user.createdAt?.toDate ? new Date(user.createdAt.toDate()).toLocaleDateString() : 'Unknown'}</p>
                            </div>

                            {/* View Public Vendor Profile */}
                            {isCorporate && (
                                <button
                                    className="btn-view-vendor-profile"
                                    onClick={() => navigate(`/${user.slug || user.uid}`)}
                                >
                                    <i className="fa-solid fa-store"></i> View My Vendor Page
                                </button>
                            )}

                            {/* Upgrade to Corporate (Individuals) */}
                            {!isCorporate && (
                                <button
                                    className="btn-upgrade-corporate"
                                    onClick={() => setShowUpgradeModal(true)}
                                >
                                    <i className="fa-solid fa-building-circle-arrow-right"></i> Upgrade to Corporate Account
                                </button>
                            )}

                            <button
                                className="btn-logout-profile"
                                onClick={() => {
                                    if (window.confirm("Are you sure you want to logout?")) {
                                        logout();
                                        navigate('/');
                                    }
                                }}
                                style={{ marginTop: '1rem', width: '100%' }}
                            >
                                <i className="fa-solid fa-right-from-bracket"></i> Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* User's Published Ads */}
            <div className="my-ads-section">
                <h3>My Published Ads ({myAds.length})</h3>
                {loadingAds ? (
                    <Spinner message="Loading your ads..." />
                ) : myAds.length > 0 ? (
                    <div className="my-ads-grid">
                        {myAds.map(ad => (
                            <div
                                key={ad.id}
                                className="ad-card-mini"
                                onClick={() => navigate(getAdLink(ad))}
                                style={{ cursor: 'pointer' }}
                            >
                                <img src={ad.image || 'https://placehold.co/600x400?text=No+Image'} alt={ad.title} className="ad-mini-image" />
                                <div className="ad-mini-content">
                                    <h4 className="ad-mini-title">{ad.title}</h4>
                                    <div className="ad-mini-price">{ad.price}</div>
                                    <div className={`ad-status-${ad.status === 'pending' ? 'pending' : 'active'}`} style={{
                                        display: 'inline-block',
                                        padding: '0.2rem 0.6rem',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        marginTop: '0.25rem',
                                        background: ad.status === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                        color: ad.status === 'pending' ? '#f59e0b' : '#10b981',
                                        textTransform: 'capitalize'
                                    }}>
                                        {ad.status === 'pending' ? 'Pending Approval' : 'Active'}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                                        Posted: {ad.createdAt?.toDate ? new Date(ad.createdAt.toDate()).toLocaleDateString() : 'Just now'}
                                    </div>
                                    <div className="ad-mini-actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className="btn-edit-mini"
                                            onClick={() => navigate(`/edit-ad/${ad.id}`)}
                                        >
                                            <i className="fa-solid fa-pen-to-square"></i> Edit
                                        </button>
                                        <button
                                            className="btn-delete-mini"
                                            onClick={() => handleDeleteAd(ad.id)}
                                        >
                                            <i className="fa-solid fa-trash"></i> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-ads">
                        <p>You haven't posted any ads yet.</p>
                        <Link to="/post-ad" className="btn-save" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', background: 'var(--color-accent)', width: 'auto' }}>
                            Post Your First Ad
                        </Link>
                    </div>
                )}
            </div>

            {/* Upgrade Modal */}
            {showUpgradeModal && (
                <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
                    <div className="upgrade-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="upgrade-modal-close" onClick={() => setShowUpgradeModal(false)}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                        <h2>Upgrade to Corporate Account</h2>
                        <p className="upgrade-modal-desc">
                            Unlock vendor features, custom profile slug, and business branding!
                        </p>
                        <form onSubmit={handleUpgradeSubmit} className="upgrade-modal-form">
                            {upgradeError && <div className="error-message" style={{ marginBottom: '1rem' }}>{upgradeError}</div>}
                            <div className="form-group">
                                <label>Business / Vendor Name</label>
                                <input
                                    type="text"
                                    value={upgradeData.vendorName}
                                    onChange={(e) => setUpgradeData({ ...upgradeData, vendorName: e.target.value })}
                                    placeholder="e.g. Elite Auto Care"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Profile Slug</label>
                                <input
                                    type="text"
                                    value={upgradeData.slug}
                                    onChange={(e) => setUpgradeData({ ...upgradeData, slug: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase() })}
                                    placeholder="e.g. elite-auto-care"
                                    required
                                />
                                <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', marginTop: '0.2rem', display: 'block' }}>
                                    This will be your public URL: <strong>/{upgradeData.slug || 'your-slug'}</strong>
                                </small>
                            </div>
                            <div className="upgrade-modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowUpgradeModal(false)}>Cancel</button>
                                <button type="submit" className="btn-save" style={{ marginTop: 0 }}>Upgrade Now</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Success Reward Popup */}
            {successMessage && (
                <div className="success-popup-overlay" onClick={() => setSuccessMessage('')}>
                    <div className="success-popup-content" onClick={(e) => e.stopPropagation()}>
                        <div className="success-icon-wrap">
                            <i className="fa-solid fa-circle-check"></i>
                        </div>
                        <h3>Success!</h3>
                        <p>{successMessage}</p>
                        <button className="btn-success-close" onClick={() => setSuccessMessage('')}>
                            Great!
                        </button>
                    </div>
                </div>
            )}


        </div>
    );
};

export default Profile;
